import type { Middleware, MiddlewareAPI } from "redux";
import { PROMOTION_CALCULATION_FINISHED, PROMOTION_CALCULATION_STARTING } from "../../../_Bootstrap/GlobalConstants";
import { SALE_CLEAR } from "../../../Actions/SellScreen/SellScreenConstants";
import type { SaleProduct } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import {
    type ApplicationEventsFromRedux,
    promotionTrackingApplicationEventsFromRedux,
    supportedApplicationEventsFromRedux,
} from "./ApplicationEvents";
import { getApplicationManager } from "./ApplicationUtilities";

const actionContainsSingleProduct = (action: object): action is { product: SaleProduct; } => {
    return "product" in action && typeof action.product === "object" && action.product !== null;
};

const actionContainsMultipleProducts = (action: object): action is { products: Array<SaleProduct>; } => {
    return "products" in action && Array.isArray(action.products);
};

class ReduxApplicationBridge {
    protected waitingForPromotionCalculation: Array<[ ApplicationEventsFromRedux, unknown ]> = [];
    protected forceSendTimeout: undefined | ReturnType<typeof setTimeout>;

    constructor(
        protected store: MiddlewareAPI
    ) {
        document.addEventListener(PROMOTION_CALCULATION_STARTING, this.handlePromotionCalculationStart);
        document.addEventListener(PROMOTION_CALCULATION_FINISHED, this.handlePromotionCalculationFinished);
    }

    /**
     * Handle an event from the application bridge and sending it to the Embedded API
     */
    protected send(type: ApplicationEventsFromRedux, action: unknown): void {
        getApplicationManager().send(type, action, undefined, 1);
    }

    /**
     * Handle receiving an event from the application bridge and sending it directly to the Embedded API
     */
    public handle(type: ApplicationEventsFromRedux, action: unknown): void {
        if(type === SALE_CLEAR) {
            // If we've received a sale clear event, force sending all the events instantly
            this.sendHeldEvents();
        }

        this.send(type, action);
    }

    /**
     * Send any held events to the Embedded API and reset the queue
     */
    protected sendHeldEvents(): void {
        clearTimeout(this.forceSendTimeout);

        const items = this.waitingForPromotionCalculation;

        this.waitingForPromotionCalculation = [];

        // Update to the most recent version of the items and then send them
        const currentSale = this.store.getState().sellscreen.sale;

        for(const [ type, action ] of items) {
            if(typeof action === "object" && action !== null) {
                if(actionContainsSingleProduct(action)) {
                    for(const product of currentSale.products) {
                        if(product.lineId === action.product.lineId) {
                            action.product = product;
                            break;
                        }
                    }
                } else if(actionContainsMultipleProducts(action)) {
                    for(let i = 0, l = action.products.length; i < l; i++) {
                        for(const product of currentSale.products) {
                            if(product.lineId === action.products[i].lineId) {
                                action.products[i] = product;
                            }
                        }
                    }
                }
            }

            this.handle(type, action);
        }
    }

    /**
     * Add an event into the queue to wait for promotion calculation events
     */
    public waitForPromotions(type: ApplicationEventsFromRedux, action: unknown): void {
        this.waitingForPromotionCalculation.push([ type, action ]);

        if(typeof this.forceSendTimeout === "undefined") {
            // Give 200ms for the promotion calculation to start, if it doesn't then we'll just send the event anyway
            this.forceSendTimeout = setTimeout(() => {
                this.sendHeldEvents();
            }, 200);
        }
    }

    /**
     * Handle the promotion calculation starting
     */
    protected handlePromotionCalculationStart = (): void => {
        // Promotion has started, we can clear the timeout
        clearTimeout(this.forceSendTimeout);
    };

    /**
     * Handle the promotion calculation finishing
     */
    protected handlePromotionCalculationFinished = (event: CustomEvent<{ calculatingIndex: number; }>): void => {
        if(event.detail.calculatingIndex <= 0) {
            // Calculation has finished
            this.sendHeldEvents();
        }
    };
}

export const applicationBridgeMiddleware: Middleware = api => {
    const reduxApplicationBridge = new ReduxApplicationBridge(api);

    return next => action => {
        const result = next(action);

        if(promotionTrackingApplicationEventsFromRedux.includes(action.type)) {
            reduxApplicationBridge.waitForPromotions(action.type, action);
        } else if(supportedApplicationEventsFromRedux.includes(action.type)) {
            reduxApplicationBridge.handle(action.type, action);
        }

        return result;
    };
};
